import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import GroupsIcon from "@mui/icons-material/Groups";
import HourglassIcon from "@mui/icons-material/HourglassTop";
import BlockIcon from "@mui/icons-material/Block";
import SendIcon from "@mui/icons-material/Send";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";

import api from "../../../api";
import TutorStatCard from "./TutorStatCard";
import PaperFilters from "./PaperFilters";
import PaperAccessRequestDialog from "./PaperAccessRequestDialog";
import { ACCESS_META, accessOf, canView, isRequestable, openPaperPdf } from "./paperAccess";

const EMPTY_FILTERS = {
  school_id: "",
  school_year_id: "",
  subject_id: "",
  category_id: "",
  access: "",
  course_plan_id: "",
  group_assigned: false,
};

const AccessChip = ({ status, note }) => {
  const meta = ACCESS_META[status] || ACCESS_META.not_requested;
  return (
    <Tooltip title={status === "rejected" && note ? note : meta.hint} arrow>
      <Chip
        size="small"
        label={meta.label}
        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, borderRadius: 1.5 }}
      />
    </Tooltip>
  );
};

/**
 * Past papers / mock exams for a tutor: search + filters, access state, per-group check, and the
 * Request Access dialog. access_status comes from the API
 * ({@see TutorPaperAccessService::statusFor()}); group_status only when a group is chosen.
 */
export default function TutorPaperAccessList({ title, endpoint, paperType, pdfUrl, showCategory = false }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({});
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({}); // id -> paper, kept across pages
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api
      .get("tutor/groups")
      .then((res) => setGroups(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setGroups([]));
    api
      .get("tutor/paper-filters", { params: { paper_type: paperType } })
      .then((res) => setOptions(res.data?.data || {}))
      .catch(() => setOptions({}));
  }, [paperType]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { page: page + 1, per_page: perPage };
    if (appliedSearch) params.search = appliedSearch;
    Object.entries(filters).forEach(([key, value]) => {
      if (value === "" || value === false) return;
      params[key] = value === true ? 1 : value;
    });
    if (!filters.course_plan_id) delete params.group_assigned;

    api
      .get(endpoint, { params })
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data;
        setRows(Array.isArray(d?.data) ? d.data : []);
        setTotal(Number(d?.total) || 0);
        setSummary(res.data?.summary || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.message || "Failed to load");
        setRows([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [endpoint, page, perPage, appliedSearch, filters, refresh]);

  const changeFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "course_plan_id" && !value ? { group_assigned: false } : {}),
    }));
    setPage(0);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const toggle = (paper) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[paper.id]) delete next[paper.id];
      else next[paper.id] = paper;
      return next;
    });

  const pageSelectable = rows.filter(isRequestable);
  const allPageSelected = pageSelectable.length > 0 && pageSelectable.every((p) => selected[p.id]);
  const somePageSelected = pageSelectable.some((p) => selected[p.id]) && !allPageSelected;

  const togglePage = () =>
    setSelected((prev) => {
      const next = { ...prev };
      pageSelectable.forEach((p) => {
        if (allPageSelected) delete next[p.id];
        else next[p.id] = p;
      });
      return next;
    });

  const selectedCount = Object.keys(selected).length;
  const groupName = useMemo(
    () => groups.find((g) => g.id === filters.course_plan_id)?.name,
    [groups, filters.course_plan_id]
  );
  const showGroupColumn = Boolean(filters.course_plan_id);
  const hasActiveFilters = appliedSearch || Object.entries(filters).some(([, v]) => v !== "" && v !== false);

  const tiles = [
    { key: "viewable", label: "Can view", icon: <LockOpenIcon />, bg: "#dcfce7", fg: "#15803d" },
    { key: "assigned", label: "Assigned to my groups", icon: <GroupsIcon />, bg: "#e0f2fe", fg: "#0369a1" },
    { key: "pending", label: "Pending requests", icon: <HourglassIcon />, bg: "#fef3c7", fg: "#b45309" },
    { key: "rejected", label: "Rejected", icon: <BlockIcon />, bg: "#fee2e2", fg: "#b91c1c" },
  ];

  const colSpan = 5 + (showGroupColumn ? 1 : 0);

  const renderAction = (paper) => {
    if (canView(paper)) {
      return (
        <Button size="small" variant="contained" disableElevation startIcon={<VisibilityIcon />} onClick={() => openPaperPdf(pdfUrl(paper.id))}>
          View
        </Button>
      );
    }
    if (isRequestable(paper)) {
      return (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SendIcon />}
          onClick={() => {
            setSelected((prev) => ({ ...prev, [paper.id]: paper }));
            setDialogOpen(true);
          }}
        >
          Request
        </Button>
      );
    }
    return (
      <Typography variant="caption" color="text.secondary">
        Awaiting admin
      </Typography>
    );
  };

  const renderGroupCell = (paper) => {
    const status = paper.group_status;
    if (status === "assigned") {
      return (
        <Chip size="small" icon={<CheckCircleIcon />} label="Assigned" color="info" sx={{ fontWeight: 700 }} />
      );
    }
    if (status && status !== "not_requested") return <AccessChip status={status} />;
    return (
      <Typography variant="caption" color="text.secondary">
        Not assigned
      </Typography>
    );
  };

  return (
    <Box sx={{ pb: selectedCount ? 10 : 0 }}>
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          borderRadius: 3,
          color: "#fff",
          background: "linear-gradient(90deg, #3B2A9F 0%, #7b3fa0 55%, #D62926 100%)",
          boxShadow: "0 8px 24px rgba(59,42,159,0.25)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 30 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {summary ? `${summary.total.toLocaleString()} available` : "Loading…"} · Items admin assigned to your
              groups open straight away; request access for anything else.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Stat tiles — click to filter */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {tiles.map((t) => {
          const active = filters.access === t.key;
          return (
            <Grid item xs={6} md={3} key={t.key}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => changeFilter("access", active ? "" : t.key)}
                onKeyDown={(e) => e.key === "Enter" && changeFilter("access", active ? "" : t.key)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 3,
                  outline: active ? `2px solid ${t.fg}` : "none",
                  transition: "transform 0.15s ease",
                  "&:hover": { transform: "translateY(-2px)" },
                }}
              >
                <TutorStatCard
                  label={t.label}
                  value={summary?.[t.key] ?? 0}
                  icon={t.icon}
                  bg={t.bg}
                  fg={t.fg}
                  loading={!summary}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <PaperFilters
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={changeFilter}
        options={options}
        groups={groups}
        showCategory={showCategory}
        onReset={resetFilters}
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer
        component={Paper}
        sx={{ borderRadius: 3, border: "1px solid #eef0f4", boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}
      >
        <Table size="small" sx={{ "& td, & th": { py: 1.25 } }}>
          <TableHead>
            <TableRow sx={{ "& th": { bgcolor: "#f8f9fc", fontWeight: 700, color: "#374151", whiteSpace: "nowrap" } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={togglePage}
                  disabled={pageSelectable.length === 0}
                  inputProps={{ "aria-label": "Select all on this page" }}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>My access</TableCell>
              {showGroupColumn && <TableCell>In {groupName || "group"}</TableCell>}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={colSpan}>
                    <Skeleton height={32} />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                  <SearchOffIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
                  <Typography fontWeight={700} sx={{ mt: 1 }}>
                    No {title.toLowerCase()} match
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Try a different search or clear the filters.
                  </Typography>
                  {hasActiveFilters && (
                    <Button variant="outlined" onClick={resetFilters}>
                      Clear filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((paper) => {
                const selectable = isRequestable(paper);
                const isSelected = Boolean(selected[paper.id]);
                const meta = [paper.school?.name, paper.school_year?.name, showCategory ? paper.category?.name : null].filter(Boolean);
                return (
                  <TableRow
                    key={paper.id}
                    hover
                    selected={isSelected}
                    sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}
                  >
                    <TableCell padding="checkbox">
                      <Tooltip title={selectable ? "" : "You already have or requested access"}>
                        <span>
                          <Checkbox
                            checked={isSelected}
                            disabled={!selectable}
                            onChange={() => toggle(paper)}
                            inputProps={{ "aria-label": `Select ${paper.name}` }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>{paper.id}</TableCell>
                    <TableCell sx={{ maxWidth: 520 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                        {paper.name}
                      </Typography>
                      {(meta.length > 0 || paper.subjects?.length > 0) && (
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {meta.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {meta.join(" · ")}
                            </Typography>
                          )}
                          {paper.subjects?.map((s) => (
                            <Chip key={s.id} label={s.name} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <AccessChip status={accessOf(paper)} note={paper.review_note} />
                    </TableCell>
                    {showGroupColumn && <TableCell>{renderGroupCell(paper)}</TableCell>}
                    <TableCell align="right">{renderAction(paper)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        )}
      </TableContainer>

      {/* Selection bar */}
      <Slide direction="up" in={selectedCount > 0} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 24,
            display: "flex",
            justifyContent: "center",
            px: 2,
            pointerEvents: "none",
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              pointerEvents: "auto",
              px: 2.5,
              py: 1.5,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              maxWidth: "100%",
            }}
          >
            <Typography variant="body2" fontWeight={700} noWrap>
              {selectedCount} selected
            </Typography>
            <Button size="small" color="inherit" onClick={() => setSelected({})}>
              Clear
            </Button>
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setDialogOpen(true)}>
              Request access
            </Button>
          </Paper>
        </Box>
      </Slide>

      <PaperAccessRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={title}
        endpoint={endpoint}
        paperType={paperType}
        pdfUrl={pdfUrl}
        groups={groups}
        initialGroupId={filters.course_plan_id}
        selected={selected}
        onRemove={(id) =>
          setSelected((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          })
        }
        onAdd={(paper) => setSelected((prev) => ({ ...prev, [paper.id]: paper }))}
        onSubmitted={() => {
          setSelected({});
          setDialogOpen(false);
          setRefresh((r) => r + 1);
        }}
      />
    </Box>
  );
}
